/**
 * 檢查日期陣列是否為連續日期
 * @param {Array} dates - 已排序的日期陣列
 * @returns {boolean} 是否為連續日期
 */
function isConsecutiveDates(dates) {
  if (dates.length <= 1) return true;

  for (let i = 1; i < dates.length; i++) {
    const prevDate = new Date(dates[i - 1]);
    const currentDate = new Date(dates[i]);
    const diffTime = currentDate - prevDate;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    // 如果相鄰日期間隔超過1天，則不連續
    if (diffDays > 1) {
      return false;
    }
  }
  return true;
}

/**
 * 將日期陣列分組為連續的日期段
 * @param {Array} dates - 已排序的日期陣列
 * @returns {Array} 連續日期段的陣列
 */
function groupConsecutiveDates(dates) {
  if (dates.length === 0) return [];

  const groups = [];
  let currentGroup = [dates[0]];

  for (let i = 1; i < dates.length; i++) {
    const prevDate = new Date(dates[i - 1]);
    const currentDate = new Date(dates[i]);
    const diffTime = currentDate - prevDate;
    const diffDays = diffTime / (1000 * 60 * 60 * 24);

    if (diffDays <= 1) {
      // 連續日期，加入當前組
      currentGroup.push(dates[i]);
    } else {
      // 不連續，開始新組
      groups.push(currentGroup);
      currentGroup = [dates[i]];
    }
  }

  // 添加最後一組
  groups.push(currentGroup);
  return groups;
}

/**
 * 格式化工作日期顯示
 * @param {string|Array} workingDate - 工作日期，可能是 JSON 字串、逗號分隔的字串或陣列
 * @returns {string} 格式化後的日期字串
 */
export function formatWorkingDate(workingDate) {
  if (!workingDate) return '- -';

  try {
    // 如果是 JSON 字串，解析它
    let dates;
    if (typeof workingDate === 'string') {
      // 先清理字串中的轉義字符和特殊字符
      let cleanedString = workingDate
        .replace(/\\/g, '')  // 移除反斜線
        .replace(/\[|\]/g, '')  // 移除方括號
        .trim();

      if (cleanedString.startsWith('[') && cleanedString.endsWith(']')) {
        dates = JSON.parse(cleanedString);
      } else if (cleanedString.includes(',')) {
        // 處理逗號分隔的日期字串
        dates = cleanedString.split(',').map(date => date.trim());
      } else {
        // 單一日期
        dates = [cleanedString];
      }
    } else if (Array.isArray(workingDate)) {
      dates = workingDate;
    } else {
      return workingDate.toString();
    }

    // 排序日期並格式化顯示
    const sortedDates = dates
      .filter(date => date && date.toString().trim())
      .map(date => {
        // 移除引號、反斜線和其他特殊字符，並格式化日期
        const cleanDate = date.toString()
          .replace(/['"\\]/g, '')  // 移除引號和反斜線
          .replace(/[\[\]]/g, '')  // 移除方括號
          .replace(/\s+/g, ' ')    // 標準化空白字符
          .trim();
        return cleanDate;
      })
      .filter(date => date && date !== '')  // 再次過濾空值
      .sort();

    if (sortedDates.length === 0) return '- -';
    if (sortedDates.length === 1) return sortedDates[0];

    // 檢查是否為連續日期
    if (isConsecutiveDates(sortedDates)) {
      // 連續日期的處理
      if (sortedDates.length <= 3) {
        return sortedDates.join(', ');
      } else {
        return `${sortedDates[0]} ~ ${sortedDates[sortedDates.length - 1]} (連續${sortedDates.length}天)`;
      }
    } else {
      // 不連續日期的處理
      if (sortedDates.length <= 3) {
        return sortedDates.join(', ');
      } else if (sortedDates.length <= 5) {
        return sortedDates.join(', ') + ` (共${sortedDates.length}天)`;
      } else {
        // 對於超過5個不連續日期，嘗試分組顯示
        const groups = groupConsecutiveDates(sortedDates);
        const groupStrings = groups.map(group => {
          if (group.length === 1) {
            return group[0];
          } else if (group.length === 2) {
            return group.join(', ');
          } else {
            return `${group[0]}~${group[group.length - 1]}`;
          }
        });

        if (groupStrings.length <= 3) {
          return groupStrings.join(', ') + ` (共${sortedDates.length}天)`;
        } else {
          return `${sortedDates[0]}, ${sortedDates[1]}, ... ${sortedDates[sortedDates.length - 1]} (共${sortedDates.length}天)`;
        }
      }
    }
  } catch (error) {
    console.error('Error formatting working date:', error);
    return workingDate;
  }
}
